import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Search } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { http, TYPES } from "../../lib/api";

export const useFacets = () => {
  const [facets, setFacets] = useState([]);
  useEffect(() => {
    http.get("/products/facets").then((r) => setFacets(Array.isArray(r.data) ? r.data : [])).catch(() => setFacets([]));
  }, []);
  return facets;
};

const trigger = "h-14 rounded-none border-0 border-white/10 bg-transparent px-5 text-left text-base focus:ring-0 md:border-r";

export const PartFinder = () => {
  const facets = useFacets();
  const nav = useNavigate();
  const [make, setMake] = useState("all");
  const [model, setModel] = useState("all");
  const [type, setType] = useState("all");
  const [q, setQ] = useState("");
  const models = facets.find((f) => f.make === make)?.models ?? [];

  const go = (e) => {
    e.preventDefault();
    const p = new URLSearchParams();
    if (make !== "all") p.set("make", make);
    if (model !== "all") p.set("model", model);
    if (type !== "all") p.set("type", type);
    if (q.trim()) p.set("q", q.trim());
    nav(`/shop?${p.toString()}`);
  };

  return (
    <motion.form
      onSubmit={go}
      data-testid="part-finder"
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 1.1, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
      className="border border-white/10 bg-[#121820]/70 backdrop-blur-xl"
    >
      <div className="flex items-center justify-between border-b border-white/10 px-5 py-3">
        <p className="font-mono text-xs uppercase tracking-[0.25em] text-slate-300">Find your part</p>
        <p className="hidden font-mono text-xs text-slate-400 sm:block">Search by vehicle or part number</p>
      </div>
      <div className="grid md:grid-cols-[1fr_1fr_1fr_1.3fr_auto]">
        <Select value={make} onValueChange={(v) => { setMake(v); setModel("all"); }}>
          <SelectTrigger data-testid="finder-make-select" className={`${trigger} border-b md:border-b-0`}><SelectValue placeholder="Make" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Any make</SelectItem>
            {facets.map((f) => <SelectItem key={f.make} value={f.make}>{f.make}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={model} onValueChange={setModel} disabled={make === "all"}>
          <SelectTrigger data-testid="finder-model-select" className={`${trigger} border-b md:border-b-0`}><SelectValue placeholder="Model" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Any model</SelectItem>
            {models.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={type} onValueChange={setType}>
          <SelectTrigger data-testid="finder-type-select" className={`${trigger} border-b md:border-b-0`}><SelectValue placeholder="Airbag type" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Any airbag type</SelectItem>
            {TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <input
          data-testid="finder-search-input"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Part number or keyword"
          className="h-14 border-b border-white/10 bg-transparent px-5 text-base outline-none placeholder:text-slate-500 md:border-b-0"
        />
        <button type="submit" data-testid="finder-submit-button" className="flex h-14 items-center justify-center gap-2 bg-[#E11D48] px-8 font-semibold transition-colors hover:bg-[#F43F5E]">
          <Search className="h-4 w-4" /> Find parts
        </button>
      </div>
    </motion.form>
  );
};
